import {useCallback, useEffect, useRef} from 'react';

import {openSidebarItemDetail} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';

export interface UseAutoOpenNewArtifactsOptions {
  /** Whether the right-sidebar layout is in use (LLM_ENABLE_RIGHT_SIDEBAR). */
  enabled: boolean;
  /** Whether the chat thread is still loading. Artifacts present once it loads are not new. */
  isLoading: boolean;
  /** The draft document's document_api_identifier, or null if the chat has no draft. */
  documentId: string | null;
  /** The active canvas's canvasDefinitionId, or null if the chat has no active canvas. */
  canvasId: string | null;
  /**
   * Whether the active canvas asked to be opened when it became active. A new canvas that did not is
   * still tracked as seen, but stays a list row.
   */
  canvasAutoOpen: boolean;
  /**
   * Whether the sidebar renders in the narrow view, where it covers the whole panel over the chat.
   * There, a draft document created from text chat stays behind the thread's draft indicator rather
   * than covering the chat unprompted.
   */
  fullWidth: boolean;
}

export interface UseAutoOpenNewArtifactsResult {
  /**
   * Opens the draft a Voice create-document tool just created, in every view. The Voice signal
   * carries no id and can arrive before or after the draft streams into the application state, so
   * this opens the newest draft if it already arrived, or else the next one that does.
   */
  openVoiceCreatedDraft: () => void;
}

/**
 * Opens the right sidebar to a draft document or canvas when the chat creates one, so new output is
 * shown rather than left behind a list row. Only ids that change after the thread loads count as
 * new, so opening an existing chat doesn't pop its artifacts open. Keyed by id rather than by every
 * application-state update, so agent edits to an already-open artifact don't pull the user back to it.
 *
 * New canvases open in every view unless written not to auto-open. New draft documents open in the split view; in the narrow view
 * only Voice-created drafts open (see openVoiceCreatedDraft).
 */
export function useAutoOpenNewArtifacts({
  enabled,
  isLoading,
  documentId,
  canvasId,
  canvasAutoOpen,
  fullWidth,
}: UseAutoOpenNewArtifactsOptions): UseAutoOpenNewArtifactsResult {
  // The ids seen at the last check; null until the thread finishes loading.
  const seenIdsRef = useRef<{documentId: string | null; canvasId: string | null} | null>(null);
  // The newest draft created since load, until a Voice signal claims it.
  const newestDocumentIdRef = useRef<string | null>(null);
  // Set when a Voice signal arrives before its draft has streamed in.
  const isVoiceDraftPendingRef = useRef(false);

  useEffect(() => {
    if (!enabled || isLoading) {
      return;
    }
    const seenIds = seenIdsRef.current;
    seenIdsRef.current = {documentId, canvasId};
    if (seenIds == null) {
      return;
    }
    if (canvasId != null && canvasId !== seenIds.canvasId && canvasAutoOpen) {
      openSidebarItemDetail(canvasId);
    }
    if (documentId != null && documentId !== seenIds.documentId) {
      const isVoiceDraft = isVoiceDraftPendingRef.current;
      isVoiceDraftPendingRef.current = false;
      newestDocumentIdRef.current = isVoiceDraft ? null : documentId;
      if (!fullWidth || isVoiceDraft) {
        openSidebarItemDetail(documentId);
      }
    }
  }, [enabled, isLoading, documentId, canvasId, canvasAutoOpen, fullWidth]);

  const documentIdRef = useRef(documentId);
  documentIdRef.current = documentId;

  const openVoiceCreatedDraft = useCallback(() => {
    const newestDocumentId = newestDocumentIdRef.current;
    if (newestDocumentId != null) {
      newestDocumentIdRef.current = null;
      openSidebarItemDetail(newestDocumentId);
      return;
    }
    // The draft hasn't streamed in yet: show the current draft, if any, until it does.
    isVoiceDraftPendingRef.current = true;
    if (documentIdRef.current != null) {
      openSidebarItemDetail(documentIdRef.current);
    }
  }, []);

  return {openVoiceCreatedDraft};
}
